import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TransButtons } from "../components/TransButtons";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { usePersistedPage } from "../lib/usePersistedPage";

/**
 * 动态/新闻编辑器（独立 news_articles 表，/api/admin/news）。
 * 列表视图：所有文章，可新建/编辑/删除/切换发布。
 * 编辑视图：元数据 + 块编辑器（标题/正文/引言/图片/要点/分隔线，中英各一份，可增删移、传图）。
 */

type Block =
  | { type: "heading"; level?: 2 | 3; text_zh: string; text_en: string }
  | { type: "paragraph"; text_zh: string; text_en: string }
  | { type: "quote"; text_zh: string; text_en: string }
  | { type: "center-quote"; text_zh: string; text_en: string }
  | { type: "center-caption"; text_zh: string; text_en: string }
  | { type: "list"; items_zh: string[]; items_en: string[] }
  | { type: "image"; url: string; align?: "normal" | "wide"; caption_zh: string; caption_en: string }
  | { type: "image-pair"; url: string; url2: string; fit?: "width" | "height"; fit2?: "width" | "height"; align?: "normal" | "wide"; captionMode?: "shared" | "each"; caption_zh: string; caption_en: string; caption2_zh?: string; caption2_en?: string }
  | { type: "divider" };

type Article = {
  id: number;
  slug: string;
  tag_cn: string; tag_en: string;
  date: string;
  title_zh: string; title_en: string;
  summary_zh: string; summary_en: string;
  cover_key: string | null;
  cover_align: "normal" | "wide";
  body: Block[];
  published: boolean;
  sort_order: number;
};

const BLOCK_LABELS: Record<string, string> = {
  heading: "标题", paragraph: "正文段落", quote: "引言", list: "要点列表",
  "center-quote": "居中引言", "center-caption": "居中署名",
  image: "图片", "image-pair": "双图并排", divider: "分隔线",
};

function fileUrl(key: string | null): string {
  if (!key) return "";
  // 上传接口已返回完整地址（COS 全 URL 或 /api/files/... 相对路径），直接用
  if (/^https?:\/\//.test(key) || key.startsWith("/")) return key;
  return `/api/files/${key}`;
}

function newBlock(type: Block["type"]): Block {
  switch (type) {
    case "heading": return { type, level: 2, text_zh: "", text_en: "" };
    case "paragraph": return { type, text_zh: "", text_en: "" };
    case "quote": return { type, text_zh: "", text_en: "" };
    case "center-quote": return { type, text_zh: "", text_en: "" };
    case "center-caption": return { type, text_zh: "", text_en: "" };
    case "list": return { type, items_zh: [], items_en: [] };
    case "image": return { type, url: "", align: "normal", caption_zh: "", caption_en: "" };
    case "image-pair": return { type, url: "", url2: "", fit: "width", fit2: "width", align: "normal", captionMode: "shared", caption_zh: "", caption_en: "", caption2_zh: "", caption2_en: "" };
    case "divider": return { type };
  }
}

const toLines = (a: string[]) => (a || []).join("\n");
const fromLines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

/** 中英并排输入（单行或多行） */
function BiField({ label, cn, en, onCn, onEn, rows }: {
  label: string; cn: string; en: string; onCn: (v: string) => void; onEn: (v: string) => void; rows?: number;
}) {
  return (
    <div className="ce-field">
      {label && <div className="ce-label">{label}</div>}
      <div className={`ce-bilingual${rows ? "" : " ce-bilingual--single"}`}>
        <div className="ce-bi-col">
          <span className="ce-bi-tag">中</span>
          {rows ? <textarea className="ce-input" rows={rows} value={cn} onChange={(e) => onCn(e.target.value)} />
                : <input className="ce-input" value={cn} onChange={(e) => onCn(e.target.value)} />}
        </div>
        <div className="ce-bi-mid">
          <TransButtons getCn={() => cn} getEn={() => en} setCn={onCn} setEn={onEn} title={label} />
        </div>
        <div className="ce-bi-col">
          <span className="ce-bi-tag">EN</span>
          {rows ? <textarea className="ce-input" rows={rows} value={en} onChange={(e) => onEn(e.target.value)} />
                : <input className="ce-input" value={en} onChange={(e) => onEn(e.target.value)} />}
        </div>
      </div>
    </div>
  );
}

/** 单个块编辑器 */
function BlockEditor({ block, onChange }: { block: Block; onChange: (b: Block) => void }) {
  const set = (patch: Partial<Block>) => onChange({ ...block, ...patch } as Block);
  const [uploading, setUploading] = useState(false);

  if (block.type === "heading") {
    return (
      <>
        <div className="ce-field">
          <div className="ce-label">层级</div>
          <select className="ce-input" style={{ width: 160 }} value={block.level ?? 2}
            onChange={(e) => set({ level: Number(e.target.value) as 2 | 3 })}>
            <option value={2}>大标题 H2</option>
            <option value={3}>小标题 H3</option>
          </select>
        </div>
        <BiField label="标题文字" cn={block.text_zh} en={block.text_en}
          onCn={(v) => set({ text_zh: v })} onEn={(v) => set({ text_en: v })} />
      </>
    );
  }
  if (block.type === "paragraph") {
    return <BiField label="正文" cn={block.text_zh} en={block.text_en} rows={4}
      onCn={(v) => set({ text_zh: v })} onEn={(v) => set({ text_en: v })} />;
  }
  if (block.type === "quote") {
    return <BiField label="引言" cn={block.text_zh} en={block.text_en} rows={2}
      onCn={(v) => set({ text_zh: v })} onEn={(v) => set({ text_en: v })} />;
  }
  if (block.type === "center-quote") {
    return <BiField label="居中引言（大号居中，带装饰引号）" cn={block.text_zh} en={block.text_en} rows={3}
      onCn={(v) => set({ text_zh: v })} onEn={(v) => set({ text_en: v })} />;
  }
  if (block.type === "center-caption") {
    return <BiField label="居中署名（小字居中，如「— 王珍 · 管理合伙人」）" cn={block.text_zh} en={block.text_en}
      onCn={(v) => set({ text_zh: v })} onEn={(v) => set({ text_en: v })} />;
  }
  if (block.type === "list") {
    return (
      <BiField label="要点（每行一条）" rows={4}
        cn={toLines(block.items_zh)} en={toLines(block.items_en)}
        onCn={(v) => set({ items_zh: fromLines(v) })} onEn={(v) => set({ items_en: fromLines(v) })} />
    );
  }
  if (block.type === "image") {
    const onPick = async (file: File) => {
      setUploading(true);
      const prev = block.url;
      try {
        const res = await api.upload<{ url: string }>("/api/admin/uploads", file, "news/");
        set({ url: res.url });
        api.dropFile(prev); // 换图：删旧图（仍被已保存版本引用则后端保留）
      } finally { setUploading(false); }
    };
    return (
      <>
        <div className="ce-field">
          <div className="ce-label">图片</div>
          {block.url
            ? <div className="ne-img-preview"><img src={fileUrl(block.url)} alt="" /></div>
            : <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>未上传图片</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
            <label className="btn" style={{ cursor: "pointer", margin: 0 }}>
              {uploading ? "上传中…" : (block.url ? "更换图片" : "上传图片")}
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }} />
            </label>
            {block.url && (
              <button className="btn" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => { const u = block.url; set({ url: "" }); api.dropFile(u); }}>移除</button>
            )}
          </div>
        </div>
        <div className="ce-field">
          <div className="ce-label">宽度</div>
          <select className="ce-input" style={{ width: 200 }} value={block.align ?? "normal"}
            onChange={(e) => set({ align: e.target.value as "normal" | "wide" })}>
            <option value="normal">常规（随正文宽）</option>
            <option value="wide">宽幅（突破栏宽）</option>
          </select>
        </div>
        <BiField label="图注（选填）" cn={block.caption_zh} en={block.caption_en}
          onCn={(v) => set({ caption_zh: v })} onEn={(v) => set({ caption_en: v })} />
      </>
    );
  }
  if (block.type === "image-pair") {
    const pick = async (file: File, slot: "url" | "url2") => {
      setUploading(true);
      const prev = block[slot];
      try {
        const res = await api.upload<{ url: string }>("/api/admin/uploads", file, "news/");
        set({ [slot]: res.url } as Partial<Block>);
        api.dropFile(prev); // 换图：删旧图
      } finally { setUploading(false); }
    };
    const slot = (label: string, key: "url" | "url2", fitKey: "fit" | "fit2") => (
      <div className="ce-field" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div className="ce-label">{label}</div>
        {/* 预览区固定高度：无论两图高度是否相同，下方按钮行都对齐 */}
        <div className="ne-pair-preview">
          {block[key]
            ? <img src={fileUrl(block[key])} alt="" />
            : <span className="muted" style={{ fontSize: 12 }}>未上传图片</span>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
          <label className="btn" style={{ cursor: "pointer", margin: 0 }}>
            {uploading ? "上传中…" : (block[key] ? "更换" : "上传")}
            <input type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f, key); e.target.value = ""; }} />
          </label>
          {block[key] && (
            <button className="btn" style={{ color: "#b04a3a", borderColor: "#b04a3a", margin: 0 }} onClick={() => { const u = block[key]; set({ [key]: "" } as Partial<Block>); api.dropFile(u); }}>移除</button>
          )}
          <select className="ce-input" style={{ flex: 1 }} value={block[fitKey] ?? "width"}
            onChange={(e) => set({ [fitKey]: e.target.value } as Partial<Block>)}>
            <option value="width">适配宽度</option>
            <option value="height">适配高度</option>
          </select>
        </div>
      </div>
    );
    const captionMode = block.captionMode ?? "shared";
    return (
      <>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {slot("左图", "url", "fit")}
          {slot("右图", "url2", "fit2")}
        </div>
        <div className="ce-field">
          <div className="ce-label">宽度</div>
          <select className="ce-input" style={{ width: 200 }} value={block.align ?? "normal"}
            onChange={(e) => set({ align: e.target.value as "normal" | "wide" })}>
            <option value="normal">常规（随正文宽）</option>
            <option value="wide">宽幅（突破栏宽）</option>
          </select>
        </div>
        <div className="ce-field">
          <div className="ce-label">图注方式</div>
          <select className="ce-input" style={{ width: 200 }} value={captionMode}
            onChange={(e) => set({ captionMode: e.target.value as "shared" | "each" })}>
            <option value="shared">共用图注（整组一条）</option>
            <option value="each">各自图注（左右各一条）</option>
          </select>
        </div>
        {captionMode === "shared" ? (
          <BiField label="图注（选填，整组共用）" cn={block.caption_zh} en={block.caption_en}
            onCn={(v) => set({ caption_zh: v })} onEn={(v) => set({ caption_en: v })} />
        ) : (
          <>
            <BiField label="左图图注（选填）" cn={block.caption_zh} en={block.caption_en}
              onCn={(v) => set({ caption_zh: v })} onEn={(v) => set({ caption_en: v })} />
            <BiField label="右图图注（选填）" cn={block.caption2_zh ?? ""} en={block.caption2_en ?? ""}
              onCn={(v) => set({ caption2_zh: v })} onEn={(v) => set({ caption2_en: v })} />
          </>
        )}
      </>
    );
  }
  return <div className="muted" style={{ fontSize: 13 }}>分隔线（无需编辑）</div>;
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^\w一-龥]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || `news-${Date.now()}`;
}

// ---------------- 列表 + 编辑 ----------------
export function NewsEditorPage() {
  const [list, setList] = useState<Article[] | null>(null);
  const [editing, setEditing] = useState<Article | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = usePersistedPage("kexi_news_page");
  const { containerRef, pageSize } = useAutoPageSize();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  // 是否从「页面文案」跳转而来（决定编辑页返回按钮的去向）
  const [fromPages, setFromPages] = useState(false);
  // 原跳转时官网预览停留的路由，返回页面文案时用它定位回去
  const [fromWebRoute, setFromWebRoute] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.get<Article[]>("/api/admin/news").then(setList).catch((e) => setError(String(e)));
  };
  useEffect(load, []);

  // 从「页面文案」右键动态详情跳转过来：?edit=<id> 自动打开对应文章
  useEffect(() => {
    const editId = params.get("edit");
    if (!editId || !list) return;
    const hit = list.find((a) => String(a.id) === editId);
    if (hit) {
      setEditing(hit);
      setFromPages(params.get("from") === "pages");
      setFromWebRoute(params.get("webroute"));
    }
    setParams({}, { replace: true }); // 消费掉 query，返回列表后不再重复打开
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const { sorted, sortKey, sortDir, toggle } = useSortable((list ?? []) as unknown as Record<string, unknown>[]);
  const pageRows = list ? (sorted as unknown as Article[]).slice((page - 1) * pageSize, page * pageSize) : null;

  const blank = (): Article => ({
    id: 0, slug: "", tag_cn: "", tag_en: "", date: "",
    title_zh: "", title_en: "", summary_zh: "", summary_en: "",
    cover_key: null, cover_align: "normal", body: [], published: true, sort_order: 0,
  });

  if (editing) {
    const back = () => {
      if (fromPages) {
        nav(fromWebRoute ? `/pages?webroute=${encodeURIComponent(fromWebRoute)}` : "/pages");
        return;
      }
      setEditing(null);
    };
    return (
      <ArticleEditor
        article={editing}
        backToPages={fromPages}
        onClose={back}
        onSaved={() => { setEditing(null); setFromPages(false); setFromWebRoute(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">动态新闻</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>官网「动态」页文章，可富文本图文排版，保存后约 1 分钟内生效</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setEditing(blank())}>+ 新建文章</button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!list && !error && <div className="muted">加载中…</div>}
      {list && list.length === 0 && <div className="muted">暂无文章，点右上「+ 新建文章」</div>}

      <div ref={containerRef}>
      {list && list.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="date" {...s} style={{ width: 96 }}>日期</SortableTh>
              <SortableTh sortKey="tag_cn" {...s} style={{ width: 110 }}>标签</SortableTh>
              <SortableTh sortKey="title_zh" {...s}>标题</SortableTh>
              <SortableTh sortKey="published" {...s} style={{ width: 80 }}>状态</SortableTh>
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((a) => (
              <tr key={a.id} className="row-link" onClick={() => setEditing(a)}>
                <td className="muted">{a.date || "—"}</td>
                <td>{a.tag_cn || <span className="muted">—</span>}</td>
                <td>{a.title_zh || <span className="muted">（未命名）</span>}</td>
                <td>{a.published ? <span className="success">已发布</span> : <span className="muted">草稿</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {list && list.length > 0 && <Pager page={page} pageSize={pageSize} total={list.length} onChange={setPage} />}
      </div>
    </div>
  );
}

function ArticleEditor({ article, onClose, onSaved, backToPages }: {
  article: Article; onClose: () => void; onSaved: () => void; backToPages?: boolean;
}) {
  const [a, setA] = useState<Article>(article);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [preview, setPreview] = useState(false);
  const blockEnd = useRef<HTMLDivElement>(null);
  const isNew = article.id === 0;

  const upd = (patch: Partial<Article>) => setA((prev) => ({ ...prev, ...patch }));
  const setBlock = (i: number, b: Block) => upd({ body: a.body.map((x, j) => (j === i ? b : x)) });
  const addBlock = (type: Block["type"]) => {
    upd({ body: [...a.body, newBlock(type)] });
    setTimeout(() => blockEnd.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const moveBlock = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= a.body.length) return;
    const copy = a.body.slice(); [copy[i], copy[j]] = [copy[j], copy[i]]; upd({ body: copy });
  };
  const removeBlock = (i: number) => {
    // 删整个块时，块里引用的图也一并删（全库复查，不误删已保存版本仍引用的）
    const b = a.body[i] as Block;
    if (b?.type === "image") api.dropFile(b.url);
    if (b?.type === "image-pair") { api.dropFile(b.url); api.dropFile(b.url2); }
    upd({ body: a.body.filter((_, j) => j !== i) });
  };

  const onCover = async (file: File) => {
    setCoverUploading(true);
    const prev = a.cover_key;
    try {
      const res = await api.upload<{ url: string }>("/api/admin/uploads", file, "news/");
      upd({ cover_key: res.url });
      api.dropFile(prev); // 换封面：删旧封面
    } finally { setCoverUploading(false); }
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const slug = a.slug.trim() || slugify(a.title_en || a.title_zh);
      const payload: any = { ...a, slug };
      if (isNew) {
        await api.post("/api/admin/news", payload);
      } else {
        delete payload.id; delete payload.created_at; delete payload.updated_at;
        await api.patch(`/api/admin/news/${a.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError && (e.body as any)?.error === "slug_conflict"
        ? "slug 已存在，请改一个" : `保存失败：${String(e)}`);
    } finally { setSaving(false); }
  };

  const del = async () => {
    try { await api.delete(`/api/admin/news/${a.id}`); onSaved(); }
    catch (e) { setError(`删除失败：${String(e)}`); setConfirmDel(false); }
  };

  return (
    <div className="ce-page">
      <div className="page-header">
        <div>
          <div className="page-title">{isNew ? "新建文章" : "编辑文章"}</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>富文本图文排版 · 中英各一份</div>
        </div>
        <div className="page-actions" style={{ alignItems: "center", gap: 12 }}>
          <button className="btn" onClick={onClose}>← {backToPages ? "返回页面文案" : "返回列表"}</button>
          {!isNew && <button className="btn btn-danger" onClick={() => setConfirmDel(true)}>删除</button>}
          <button className="btn btn-primary btn-save" disabled={saving} onClick={save}>{saving ? "保存中…" : "保 存"}</button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 14 }}>{error}</div>}

      {/* 元数据 */}
      <div className="ne-meta">
        <div className="je-meta-grid">
          <div className="ce-field">
            <div className="ce-label">日期</div>
            <input className="ce-input" placeholder="2026.06.15" value={a.date} onChange={(e) => upd({ date: e.target.value })} />
          </div>
          <div className="ce-field">
            <div className="ce-label">slug（网址，留空自动生成）</div>
            <input className="ce-input" placeholder="auto" value={a.slug} onChange={(e) => upd({ slug: e.target.value })} />
          </div>
          <div className="ce-field">
            <div className="ce-label">发布状态</div>
            <select className="ce-input" value={a.published ? "1" : "0"} onChange={(e) => upd({ published: e.target.value === "1" })}>
              <option value="1">已发布</option>
              <option value="0">草稿（不公开）</option>
            </select>
          </div>
        </div>

        <BiField label="标签" cn={a.tag_cn} en={a.tag_en} onCn={(v) => upd({ tag_cn: v })} onEn={(v) => upd({ tag_en: v })} />
        <BiField label="标题" cn={a.title_zh} en={a.title_en} onCn={(v) => upd({ title_zh: v })} onEn={(v) => upd({ title_en: v })} />
        <BiField label="摘要（列表与详情顶部展示）" rows={2} cn={a.summary_zh} en={a.summary_en}
          onCn={(v) => upd({ summary_zh: v })} onEn={(v) => upd({ summary_en: v })} />

        <div className="ce-field">
          <div className="ce-label">封面图（选填）</div>
          {a.cover_key && <div className="ne-img-preview"><img src={fileUrl(a.cover_key)} alt="" /></div>}
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <label className="btn" style={{ cursor: "pointer" }}>
              {coverUploading ? "上传中…" : (a.cover_key ? "更换封面" : "上传封面")}
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onCover(f); e.target.value = ""; }} />
            </label>
            {a.cover_key && <button className="btn" onClick={() => { const u = a.cover_key; upd({ cover_key: null }); api.dropFile(u); }}>移除封面</button>}
          </div>
          {a.cover_key && (
            <div style={{ marginTop: 10 }}>
              <div className="ce-label">封面宽度</div>
              <select className="ce-input" style={{ width: 200 }} value={a.cover_align}
                onChange={(e) => upd({ cover_align: e.target.value as "normal" | "wide" })}>
                <option value="normal">常规（随正文宽）</option>
                <option value="wide">宽幅（突破栏宽）</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* 正文块 */}
      <div className="ne-blocks-head">正文排版</div>
      <div className="ne-blocks">
        {a.body.map((b, i) => (
          <div className="ne-block" key={i}>
            <div className="ne-block-head">
              <span className="ne-block-type">{BLOCK_LABELS[b.type]}</span>
              <span className="ce-array-tools">
                <button className="ce-tool" title="上移" disabled={i === 0} onClick={() => moveBlock(i, -1)}>↑</button>
                <button className="ce-tool" title="下移" disabled={i === a.body.length - 1} onClick={() => moveBlock(i, 1)}>↓</button>
                <button className="ce-tool ce-tool-danger" title="删除" onClick={() => removeBlock(i)}>×</button>
              </span>
            </div>
            <div className="ne-block-body">
              <BlockEditor block={b} onChange={(nb) => setBlock(i, nb)} />
            </div>
          </div>
        ))}
        {a.body.length === 0 && <div className="muted" style={{ padding: "16px 0" }}>还没有内容块，从下方添加</div>}
        <div ref={blockEnd} />
      </div>

      <div className="ne-add-bar">
        <span className="muted" style={{ fontSize: 12, marginRight: 4 }}>添加块：</span>
        {(["heading", "paragraph", "quote", "center-quote", "center-caption", "list", "image", "image-pair", "divider"] as Block["type"][]).map((tp) => (
          <button key={tp} className="btn btn-xs" onClick={() => addBlock(tp)}>+ {BLOCK_LABELS[tp]}</button>
        ))}
      </div>

      <ConfirmDialog
        open={confirmDel}
        title="删除文章"
        message={<>确认删除「{a.title_zh || a.slug}」？此操作不可恢复。</>}
        confirmLabel="删除" danger
        onConfirm={del} onCancel={() => setConfirmDel(false)}
      />

      <EditorFabs onPreview={() => setPreview(true)} />

      {preview && <ArticlePreview article={a} onClose={() => setPreview(false)} />}
    </div>
  );
}

// 编辑页右侧悬浮按钮：预览（右侧垂直中心）+ 回到顶部（右下角），呼应官网 fab
function EditorFabs({ onPreview }: { onPreview: () => void }) {
  const scroller = () => document.querySelector(".main") as HTMLElement | null;
  const toTop = () => (scroller() ?? window).scrollTo({ top: 0, behavior: "smooth" });
  const fab: React.CSSProperties = {
    position: "fixed", right: 20,
    width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--bg-1, #0e1430)", border: "1px solid var(--gold, #c9a86b)",
    color: "var(--gold, #c9a86b)", cursor: "pointer", borderRadius: 4,
    boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 80,
  };
  return (
    <>
      <button style={{ ...fab, top: "50%", transform: "translateY(-50%)" }}
        title="预览" aria-label="预览" onClick={onPreview}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5Z" stroke="currentColor" strokeWidth="1.2"/>
          <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/>
        </svg>
      </button>
      <button style={{ ...fab, bottom: 24 }}
        title="回到顶部" aria-label="回到顶部" onClick={toTop}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 11V3M3 7l4-4 4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </>
  );
}

// ---------------- 文章预览弹窗（复刻官网详情页排版，所见即所得，无需保存） ----------------
const PREVIEW_CSS = `
.apv-backdrop { position: fixed; inset: 0; z-index: 1000; background: rgba(7,15,34,0.6); display: flex; flex-direction: column; }
.apv-bar { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; gap: 16px; padding: 12px; background: #0e1430; color: #f5f1e8; }
.apv-bar .apv-seg { display: inline-flex; border: 1px solid rgba(245,241,232,0.3); border-radius: 4px; overflow: hidden; }
.apv-bar .apv-seg button { background: transparent; color: #cdd3e6; border: none; padding: 5px 16px; cursor: pointer; font-size: 13px; }
.apv-bar .apv-seg button.on { background: #c9a86b; color: #0e1430; }
.apv-bar .apv-close { position: absolute; right: 16px; background: transparent; border: 1px solid rgba(245,241,232,0.3); color: #f5f1e8; width: 30px; height: 30px; border-radius: 4px; cursor: pointer; font-size: 18px; line-height: 1; }
.apv-scroll { flex: 1 1 auto; overflow: auto; background: #f3efe3; }
.apv-doc { max-width: 760px; margin: 0 auto; padding: 56px 32px 96px; color: #1b2440; font-family: "Songti SC","Noto Serif SC",serif; }
.apv-meta { font-family: ui-monospace,monospace; font-size: 13px; letter-spacing: .08em; color: #b08948; margin-bottom: 16px; }
.apv-title { font-size: 40px; line-height: 1.25; font-weight: 600; margin: 0 0 20px; }
.apv-summary { font-size: 17px; line-height: 1.7; color: #50596f; margin: 0 0 8px; }
.apv-doc img { width: 100%; display: block; border: 1px solid rgba(27,36,64,0.12); }
.apv-cover { margin: 28px 0; }
.apv-h2 { font-size: 27px; margin: 40px 0 16px; border-left: 3px solid #c9a86b; padding-left: 14px; }
.apv-h3 { font-size: 21px; margin: 32px 0 12px; }
.apv-p { font-size: 17px; line-height: 1.9; margin: 18px 0; }
.apv-quote { font-size: 20px; line-height: 1.7; font-style: italic; color: #b08948; border-left: 3px solid #c9a86b; padding: 4px 0 4px 18px; margin: 28px 0; }
.apv-cquote { text-align: center; margin: 56px 0 0; }
.apv-cquote-mark { font-family: Georgia, "Songti SC", serif; font-size: 64px; color: #c9a86b; line-height: 0.4; display: block; }
.apv-cquote p { font-size: 26px; line-height: 1.6; font-weight: 400; max-width: 640px; margin: 28px auto 0; color: #1b2440; }
.apv-ccaption { text-align: center; font-family: ui-monospace, monospace; font-size: 13px; letter-spacing: .1em; color: #b08948; margin: 24px 0 48px; }
.apv-list { font-size: 17px; line-height: 1.9; padding-left: 22px; margin: 18px 0; }
.apv-fig { margin: 36px 0; }
.apv-fig figcaption { margin-top: 12px; font-size: 13px; color: #7a8198; text-align: center; }
/* 宽幅：突破 760 阅读栏，以视口中线居中（apv-doc 居中，left:50% 即视口中线） */
.apv-wide { width: min(1100px, calc(100vw - 48px)); position: relative; left: 50%; transform: translateX(-50%); }
.apv-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start; }
.apv-pair-cell { display: flex; flex-direction: column; min-width: 0; }
/* 适配高度：统一展示高度，填满裁切（两图等高）。适配宽度：按原比例完整显示。 */
.apv-pair-img { width: 100%; display: block; }
.apv-pair-img.is-cover { height: 320px; object-fit: cover; }
.apv-pair-img.is-contain { height: auto; }
.apv-paircap { margin-top: 10px; font-size: 13px; color: #7a8198; text-align: center; }
.apv-hr { border: none; border-top: 1px solid rgba(27,36,64,0.15); margin: 40px 0; }
.apv-top { position: fixed; right: 24px; bottom: 24px; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; background: #0e1430; border: 1px solid #c9a86b; color: #c9a86b; border-radius: 4px; cursor: pointer; box-shadow: 0 8px 24px rgba(0,0,0,0.3); transition: opacity .3s; z-index: 1001; }
.apv-empty { color: #7a8198; padding: 40px 0; text-align: center; }
`;

function ArticlePreview({ article, onClose }: { article: Article; onClose: () => void }) {
  const [lang, setLang] = useState<"zh" | "en">("zh");
  const zh = lang === "zh";
  const tx = (cn: string, en: string) => (zh ? cn : en) || cn || en || "";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const a = article;
  const title = tx(a.title_zh, a.title_en);
  const summary = tx(a.summary_zh, a.summary_en);
  const tag = tx(a.tag_cn, a.tag_en);

  return (
    <div className="apv-backdrop" onClick={onClose}>
      <style>{PREVIEW_CSS}</style>
      <div className="apv-bar" onClick={(e) => e.stopPropagation()}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>预览（未保存内容，所见即所得）</span>
        <span className="apv-seg">
          <button className={zh ? "on" : ""} onClick={() => setLang("zh")}>中文</button>
          <button className={!zh ? "on" : ""} onClick={() => setLang("en")}>EN</button>
        </span>
        <button className="apv-close" onClick={onClose}>×</button>
      </div>
      <div className="apv-scroll" ref={scrollRef} onClick={onClose}
        onScroll={(e) => setShowTop((e.target as HTMLElement).scrollTop > 300)}>
        <article className="apv-doc" onClick={(e) => e.stopPropagation()}>
          <div className="apv-meta">{a.date}{tag ? `  ·  ${tag}` : ""}</div>
          <h1 className="apv-title">{title || (zh ? "（未填写标题）" : "(untitled)")}</h1>
          {summary && <p className="apv-summary">{summary}</p>}
          {a.cover_key && <div className={`apv-cover${a.cover_align === "wide" ? " apv-wide" : ""}`}><img src={fileUrl(a.cover_key)} alt="" /></div>}
          {a.body.length === 0 && <div className="apv-empty">{zh ? "暂无正文内容" : "No content yet"}</div>}
          {a.body.map((b, i) => {
            if (b.type === "heading") return <h2 key={i} className={b.level === 3 ? "apv-h3" : "apv-h2"}>{tx(b.text_zh, b.text_en)}</h2>;
            if (b.type === "paragraph") return <p key={i} className="apv-p">{tx(b.text_zh, b.text_en)}</p>;
            if (b.type === "quote") return <blockquote key={i} className="apv-quote">{tx(b.text_zh, b.text_en)}</blockquote>;
            if (b.type === "center-quote") return (
              <div key={i} className="apv-cquote">
                <span className="apv-cquote-mark">"</span>
                <p>{tx(b.text_zh, b.text_en)}</p>
              </div>
            );
            if (b.type === "center-caption") return <div key={i} className="apv-ccaption">{tx(b.text_zh, b.text_en)}</div>;
            if (b.type === "list") return <ul key={i} className="apv-list">{(zh ? b.items_zh : b.items_en).map((it, j) => <li key={j}>{it}</li>)}</ul>;
            if (b.type === "image") return (
              <figure key={i} className={`apv-fig${b.align === "wide" ? " apv-wide" : ""}`}>
                {b.url ? <img src={fileUrl(b.url)} alt="" /> : <div className="apv-empty">未上传图片</div>}
                {tx(b.caption_zh, b.caption_en) && <figcaption>{tx(b.caption_zh, b.caption_en)}</figcaption>}
              </figure>
            );
            if (b.type === "image-pair") {
              // 两图并排等高（grid 行 stretch）；fit=height 填满裁切，fit=width 完整显示
              const imgClass = (f?: "width" | "height") => `apv-pair-img ${f === "height" ? "is-cover" : "is-contain"}`;
              const each = b.captionMode === "each";
              const cap1 = tx(b.caption_zh, b.caption_en);
              const cap2 = tx(b.caption2_zh ?? "", b.caption2_en ?? "");
              return (
                <figure key={i} className={`apv-fig${b.align === "wide" ? " apv-wide" : ""}`}>
                  <div className="apv-pair">
                    {([[b.url, b.fit, cap1], [b.url2, b.fit2, cap2]] as const).map(([u, f, cap], k) => (
                      <div key={k} className="apv-pair-cell">
                        {u ? <img src={fileUrl(u)} alt="" className={imgClass(f)} /> : <div className="apv-empty">未上传</div>}
                        {each && cap && <div className="apv-paircap">{cap}</div>}
                      </div>
                    ))}
                  </div>
                  {!each && cap1 && <figcaption>{cap1}</figcaption>}
                </figure>
              );
            }
            if (b.type === "divider") return <hr key={i} className="apv-hr" />;
            return null;
          })}
        </article>
      </div>
      <button
        className="apv-top"
        title="回到顶部" aria-label="回到顶部"
        style={{ opacity: showTop ? 1 : 0, pointerEvents: showTop ? "auto" : "none" }}
        onClick={(e) => { e.stopPropagation(); scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" }); }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 11V3M3 7l4-4 4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}
